// ODOO-4440/4646: общая механика иерархической легенды (группа → серии) для
// AttrChart и AttrBubbleChart. Чистые функции без this: владельцу (чарт/бабл)
// группы нужны ДО монтирования панели (v-if кнопки легенды считается по ним),
// панель ChartLegendPanel живёт только пока открыта.
import * as wjChart from '@mescius/wijmo.chart'

// хвосты плавающей точки (80474.85436522) в легенде не нужны:
// до 100 — 2 знака, дальше целые
export function legendRoundValue(v) {
    const n = +v
    if (!Number.isFinite(n)) return v
    return Math.abs(n) < 100 ? Math.round(n * 100) / 100 : Math.round(n)
}

export function legendSeriesColor(seriesList, palette, series, attr) {
    const fill = attr?.cssStyleChart?.fill
        || (series.style && typeof series.style === 'object' && series.style.fill)
    if (fill) {
        // ODOO-4027: wijmo-градиент 'l(x1,y1,x2,y2)#a-#b' — не валидный CSS,
        // маркер легенды терял цвет. Переводим в css linear-gradient.
        const grad = typeof fill === 'string' && fill.match(/^l\([^)]*\)(#[0-9a-fA-F]{3,8})-(#[0-9a-fA-F]{3,8})$/)
        if (grad) return `linear-gradient(180deg, ${grad[1]}, ${grad[2]})`
        return fill
    }
    // дефолтная палитра wijmo — цвет назначается по индексу серии
    const pal = (palette && palette.length && palette)
        || wjChart.Palettes?.standard
        || ['#88bde6', '#fbb258', '#90cd97', '#f6aac9', '#bfa554', '#bc99c7', '#eddd46', '#f07e6e', '#8c8c8c']
    return pal[seriesList.indexOf(series) % pal.length]
}

// IE 2026 09 12 / ODOO-… (замечание Игоря): Alt+перетаскивание строки легенды — перенос поля серии в дереве полей: узел с binding
// `moving` встаёт перед/после узла `target` в ТОМ ЖЕ родительском массиве (группа легенды = родитель в дереве).
// Возвращает true, если перенос сделан; в разных родителях — false (ничего не меняем)
export function moveNodeInTree(nodes, moving, target, before) {
    if (!Array.isArray(nodes) || moving === target) return false
    const i = nodes.findIndex(n => n && n.binding === moving), j = nodes.findIndex(n => n && n.binding === target)
    if (i >= 0 && j >= 0) {
        const [node] = nodes.splice(i, 1)
        const k = nodes.findIndex(n => n && n.binding === target)
        nodes.splice(before ? k : k + 1, 0, node)
        return true
    }
    for (const n of nodes) if (n && Array.isArray(n.attrs) && moveNodeInTree(n.attrs, moving, target, before)) return true
    return false
}

// Иерархия легенды: {key, title, checked, indeterminate, expanded, collapsible,
// series:[{binding, name, color, visible}]}. Порядок серий разреза = фактический
// порядок отрисовки (Z): ↑/↓ и Alt+стрелки переставляют серию в series —
// легенда следует за ним.
const LEGEND_COLLAPSE_OVER = 30   // IE 2026 09 14 / ODOO-5800: разрез длиннее — свёрнут по умолчанию

export function buildLegendGroups({ series: seriesList, palette, rows, attrsByBinding, tree, expanded }) {
    if (!seriesList) return []

    // отрисованные серии по binding; visibility 3 (Hidden — служебные chartGroup) не показываем
    const rendered = new Map()
    for (const s of seriesList) {
        if (s.visibility === 3) continue
        rendered.set(s.binding, s)
    }

    const groups = [], groupByKey = {}, used = new Set()
    // как в окне расшифровки: серии с полностью пустым рядом (нет ни одного
    // ненулевого значения — напр. страны без оборотов при фильтре) не показываем
    const hasAnyValue = (b) => (rows || []).some(r => {
        const v = r?.[b]
        return v !== null && v !== undefined && v !== '' && +v !== 0
    })
    const pushItem = (groupKey, groupTitle, series, attr) => {
        if (!hasAnyValue(series.binding)) { used.add(series.binding); return }
        let g = groupByKey[groupKey]
        if (!g) {
            g = groupByKey[groupKey] = { key: groupKey, title: groupTitle || '', series: [] }
            groups.push(g)
        }
        g.series.push({
            binding: series.binding,
            name: series.name || series.binding,
            color: legendSeriesColor(seriesList, palette, series, attr),
            visible: series.visibility === 0 || series.visibility === 1, // Visible | Plot
        })
        used.add(series.binding)
    }

    const walk = (nodes, groupTitle, groupKey) => {
        for (const node of nodes || []) {
            if (!node || typeof node !== 'object') continue
            if (node.binding) {
                // обычная серия — сама мера в отрисованных.
                // ODOO-4646: chart_down-мера БЕЗ разреза отрисована ключом
                // <binding>__down — по исходному binding она в rendered не
                // находилась и уезжала в безымянную группу-подстраховку
                const s = rendered.get(node.binding) || rendered.get(node.binding + '__down')
                if (s && !used.has(s.binding)) {
                    pushItem(groupKey, groupTitle, s, attrsByBinding[s.binding] || node)
                }
                // порождённые серии разреза этой меры → группа = мера
                const derived = []
                for (const [b2, s2] of rendered) {
                    if (used.has(b2)) continue
                    const a2 = attrsByBinding[b2]
                    if (a2?.series_binding === node.binding) derived.push([s2, a2])
                }
                if (derived.length) {
                    // порядок = фактический порядок отрисовки (Z)
                    const zIndex = new Map(seriesList.map((sr, iz) => [sr.binding, iz]))
                    derived.sort((x, y) => (zIndex.get(x[0].binding) ?? 0) - (zIndex.get(y[0].binding) ?? 0))
                    for (const [s2, a2] of derived) {
                        pushItem(`measure:${node.binding}`, node.title || node.binding, s2, a2)
                    }
                }
            }
            if (node.attrs) {
                walk(node.attrs, node.title || groupTitle, node.title ? `node:${groupKey}/${node.title}` : groupKey)
            }
        }
    }
    // источник иерархии — attrs-дерево конфига виджета (как в гридах), не хардкод
    walk(tree, '', '')
    // подстраховка: серии, не найденные в конфиге, — вне групп
    for (const [b, s] of rendered) {
        if (!used.has(b)) pushItem('', '', s, attrsByBinding[b])
    }

    for (const g of groups) {
        const visibleCount = g.series.filter(s => s.visible).length
        g.checked = visibleCount === g.series.length && g.series.length > 0
        g.indeterminate = visibleCount > 0 && visibleCount < g.series.length
        // ODOO-4137: сворачивать есть смысл только у группы с заголовком и
        // несколькими сериями. Серии разреза (measure:*) свёрнуты по умолчанию —
        // их бывают десятки и легенда съедала четверть виджета; остальные группы
        // раскрыты. Ручной выбор пользователя (expanded) всегда главнее.
        g.collapsible = !!g.title && g.series.length > 1
        // IE 2026 09 14 / ODOO-5800 (замечание Игоря): легенда теперь отдельное окно, свёрнутая группа разреза в нём
        // неинформативна — раскрываем сразу; сворачиваем по умолчанию только очень длинные разрезы (> LEGEND_COLLAPSE_OVER серий)
        // ODOO-4440 (12.08): ручной выбор (шеврон, петличка «свернуть все») главнее дефолта
        // и действует И на группы с одной серией — collapsible влияет только на дефолт
        const collapsedByDefault = g.key.startsWith('measure:') && g.series.length > LEGEND_COLLAPSE_OVER
        g.expanded = (expanded || {})[g.key] ?? !collapsedByDefault
    }
    return groups
}
